import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { usePatient } from '@/hooks/usePatients';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { useCreateInvoiceDraft } from '@/hooks/useInvoices';
import { useClinic } from '@/hooks/useClinicSettings';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export function NewInvoice() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeClinicId } = useActiveClinic();

  const initialPatientId = searchParams.get('patient_id');
  const initialClinicId = searchParams.get('clinic_id');
  const returnTo = searchParams.get('return_to');

  const { data: initialPatient, isLoading: loadingInitialPatient } = usePatient(initialPatientId);
  const { data: clinic } = useClinic(initialClinicId ?? activeClinicId);

  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientPickerItem | null>(null);
  const [invoiceClinicId, setInvoiceClinicId] = useState<string | null>(initialClinicId);
  const [dueAt, setDueAt] = useState('');
  const [notes, setNotes] = useState('');

  const pickerClinicId = invoiceClinicId ?? activeClinicId;
  const picker = usePatientPicker(pickerClinicId, search);
  const create = useCreateInvoiceDraft();

  // Pre-fill due date with today + payment_terms_days
  useEffect(() => {
    if (!clinic || dueAt) return; // Don't override if already set
    const paymentTermsDays = clinic.payment_terms_days ?? 14;
    const defaultDueDate = new Date();
    defaultDueDate.setDate(defaultDueDate.getDate() + paymentTermsDays);
    setDueAt(defaultDueDate.toISOString().slice(0, 10));
  }, [clinic, dueAt]);

  useEffect(() => {
    if (!initialPatient) return;
    setSelectedPatient({
      id: initialPatient.id,
      pat_number: initialPatient.pat_number,
      first_name: initialPatient.first_name,
      last_name: initialPatient.last_name,
      email: initialPatient.email,
      phone: initialPatient.phone,
    });
    setInvoiceClinicId(initialClinicId ?? initialPatient.clinic_id);
  }, [initialPatient, initialClinicId]);

  const handleBack = () => {
    navigate(returnTo && returnTo.startsWith('/') ? returnTo : '/economy/invoices');
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const clinicId = invoiceClinicId ?? activeClinicId;
    if (!clinicId) return;
    if (!selectedPatient) {
      toast.error(t('invoices.patient_required'));
      return;
    }
    try {
      const inv = await create.mutateAsync({
        clinic_id: clinicId,
        patient_id: selectedPatient.id,
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t('invoices.toast_created'));
      navigate(`/invoices/${inv.id}`);
    } catch (err) {
      console.error('create invoice draft failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="mx-auto max-w-3xl px-6">
          <button
            type="button"
            onClick={handleBack}
            className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            {returnTo?.startsWith('/patients/')
              ? t('invoices.back_to_patient')
              : t('invoices.back_to_list')}
          </button>

          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
            {t('invoices.new_invoice')}
          </h2>

          <form
            onSubmit={onSubmit}
            className="mt-6 space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('invoices.patient_picker_label')}
              </label>
              {loadingInitialPatient && initialPatientId && !selectedPatient ? (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </div>
              ) : selectedPatient ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800">
                  <span className="text-gray-900 dark:text-white">
                    {selectedPatient.first_name} {selectedPatient.last_name}
                    {selectedPatient.pat_number && (
                      <span className="ml-2 font-mono text-xs text-gray-500">
                        {selectedPatient.pat_number}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPatient(null);
                      setInvoiceClinicId(activeClinicId ?? null);
                    }}
                    className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                  >
                    {t('common.edit')}
                  </button>
                </div>
              ) : (
                <div className="mt-1">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('invoices.patient_picker_placeholder')}
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  {picker.isLoading && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                      <Loader2 className="h-3 w-3 animate-spin" /> {t('common.loading')}
                    </div>
                  )}
                  {picker.data && picker.data.length > 0 && (
                    <div className="mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                      {picker.data.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setSelectedPatient(p);
                            setInvoiceClinicId(pickerClinicId ?? activeClinicId ?? null);
                            setSearch('');
                          }}
                          className="flex w-full items-center justify-between border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                        >
                          <span className="text-gray-900 dark:text-white">
                            {p.first_name} {p.last_name}
                          </span>
                          <span className="font-mono text-xs text-gray-500">
                            {p.pat_number ?? ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <Input
              label={t('invoices.field_due_at')}
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('invoices.field_notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleBack}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={create.isPending || !selectedPatient || loadingInitialPatient}
              >
                {create.isPending ? t('common.saving') : t('invoices.actions_save_draft')}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
